import React from "react";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useState, useCallback, useRef, useEffect } from "react";
import { differenceInDays, parseISO, isValid, addDays, format } from "date-fns";
import { cn } from "@/lib/utils";
import type { VesselAssignmentWithRelations } from "@/hooks/useVesselAssignments";
import { getLotForAssignment } from "@/hooks/useVesselAssignments";
import { usePicklistValues } from "@/hooks/usePicklistValues";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Wine } from "lucide-react";
import { useVolumeUnit } from "@/hooks/useOrganization";
import { getVarietyColor, VARIETY_DEFAULT_COLOR } from "@/hooks/useVarietyColorMap";

interface AssignmentBarProps {
  assignment: VesselAssignmentWithRelations;
  startDate: Date;
  endDate: Date;
  dayWidth: number;
  rowHeight: number;
  /** Single-click: select the bar */
  onSelect: () => void;
  /** Double-click: open edit popover */
  onEditRequest: (assignment: VesselAssignmentWithRelations, rect: DOMRect) => void;
  isSelected?: boolean;
  isDragOverlay?: boolean;
  onResize?: (assignmentId: string, newEndDate: string) => void;
  onResizeStart?: (assignmentId: string, newStartDate: string) => void;
  onMoveToOnDeck?: (assignment: VesselAssignmentWithRelations) => void;
  onAssignmentContextMenu?: (assignment: VesselAssignmentWithRelations, x: number, y: number) => void;
  isOtherDragging?: boolean;
  colorMap?: Record<string, string>;
  nameMap?: Record<string, string>;
  isConnectionSource?: boolean;
  isConnectionTarget?: boolean;
  /** Same-day turnover split: 'split-out' bar ends at mid-day, 'split-in' bar starts at mid-day. */
  splitRole?: 'split-out' | 'split-in' | null;
  /** Customer portal: no dragging. Resize/context menu are already off when their callbacks are omitted. */
  readOnly?: boolean;
  /** Show assignment notes in the tooltip (customer portal: staff notes on the client's own bars). */
  showNotes?: boolean;
}

// Out-reason label map for inline display
function getOutReasonLabel(
  assignment: VesselAssignmentWithRelations,
  barLabelMap: Record<string, string>,
): string | null {
  const hasEndDate = assignment.projected_empty_date || assignment.actual_empty_date;
  if (!hasEndDate) return null;
  if (!assignment.empty_reason) return '?';
  return barLabelMap[assignment.empty_reason] || assignment.empty_reason.toUpperCase();
}

// Status-based styling
const STATUS_STYLES: Record<string, string> = {
  planned: '',
  active: '',
  completed: 'opacity-50 saturate-50', // Faded for completed
  cancelled: 'opacity-30 line-through',
};

// Helper to darken a hex color for hover state
function darkenHex(hex: string, amount: number = 0.15): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.max(0, ((num >> 16) & 0xff) * (1 - amount));
  const g = Math.max(0, ((num >> 8) & 0xff) * (1 - amount));
  const b = Math.max(0, (num & 0xff) * (1 - amount));
  return `#${((1 << 24) + (Math.round(r) << 16) + (Math.round(g) << 8) + Math.round(b)).toString(16).slice(1)}`;
}


export function AssignmentBar({
  assignment,
  startDate,
  endDate,
  dayWidth,
  rowHeight,
  onSelect,
  onEditRequest,
  isSelected = false,
  isDragOverlay = false,
  onResize,
  onResizeStart,
  onMoveToOnDeck,
  onAssignmentContextMenu,
  isOtherDragging = false,
  colorMap = {},
  nameMap = {},
  splitRole = null,
  isConnectionSource = false,
  isConnectionTarget = false,
  readOnly = false,
  showNotes = false,
}: AssignmentBarProps) {
  // Another client's bar on the customer-portal Tank Schedule — details were masked server-side.
  const isMasked = assignment.is_mine === false;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: assignment.id,
    data: { assignment },
    disabled: readOnly || isMasked,
  });
  const { volumeAbbrev } = useVolumeUnit();
  const lot = getLotForAssignment(assignment);
  // Resolve display varietal: VV code from lot code is authoritative (immune to stale text).
  // Falls back to stored lot.varietal when code is short or VV doesn't resolve.
  const displayVarietal = (() => {
    if (lot?.code && lot.code.length >= 6) {
      const name = nameMap[lot.code.slice(4, 6).toLowerCase()];
      if (name) return name;
    }
    return lot?.varietal ?? null;
  })();
  const { data: emptyReasonValues = [] } = usePicklistValues("empty_reason");
  const barLabelMap = Object.fromEntries(
    emptyReasonValues.map(v => [v.value, v.bar_label ?? v.value.toUpperCase()])
  );

  // Resize state
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDelta, setResizeDelta] = useState(0);
  const resizeStartRef = useRef<{ x: number; originalWidth: number } | null>(null);
  const justResizedRef = useRef(false);

  // Left-edge resize state (adjusts fill_date)
  const [isResizingLeft, setIsResizingLeft] = useState(false);
  const [resizeLeftDelta, setResizeLeftDelta] = useState(0);
  const resizeLeftStartRef = useRef<{ x: number } | null>(null);

  // Parse dates - these come from optimisticAssignments so they're already updated after confirm
  const fillDate = parseISO(assignment.fill_date);
  const baseEmptyDate = assignment.projected_empty_date
    ? parseISO(assignment.projected_empty_date)
    : endDate;

  // During resize, snap to day boundaries (trunc so you must cross the boundary fully)
  const daysDeltaFromResize = Math.trunc(resizeDelta / dayWidth);

  const emptyDate = baseEmptyDate;

  // Handle left-edge resize start (adjusts fill_date)
  const handleLeftResizeStart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizingLeft(true);
    setResizeLeftDelta(0);
    resizeLeftStartRef.current = { x: e.clientX };
  }, []);

  // Handle resize start
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeDelta(0);
    resizeStartRef.current = { x: e.clientX, originalWidth: 0 };
  }, []);

  // Right-edge resize mouse effects
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeStartRef.current) return;
      setResizeDelta(e.clientX - resizeStartRef.current.x);
    };

    const handleMouseUp = () => {
      if (resizeStartRef.current) {
        const daysDelta = Math.trunc(resizeDelta / dayWidth);
        if (daysDelta !== 0) {
          const newEndDate = addDays(baseEmptyDate, daysDelta);
          if (newEndDate >= fillDate) {
            onResize?.(assignment.id, format(newEndDate, 'yyyy-MM-dd'));
          }
        }
      }
      setIsResizing(false);
      resizeStartRef.current = null;
      justResizedRef.current = true;
      setTimeout(() => { justResizedRef.current = false; }, 100);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeDelta, dayWidth, baseEmptyDate, fillDate, assignment.id, onResize]);

  // Left-edge resize mouse effects
  useEffect(() => {
    if (!isResizingLeft) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeLeftStartRef.current) return;
      setResizeLeftDelta(e.clientX - resizeLeftStartRef.current.x);
    };

    const handleMouseUp = () => {
      if (resizeLeftStartRef.current) {
        const daysDelta = Math.trunc(resizeLeftDelta / dayWidth);
        if (daysDelta !== 0) {
          const newStartDate = addDays(fillDate, daysDelta);
          if (newStartDate < baseEmptyDate) {
            onResizeStart?.(assignment.id, format(newStartDate, 'yyyy-MM-dd'));
          }
        }
      }
      setIsResizingLeft(false);
      resizeLeftStartRef.current = null;
      justResizedRef.current = true;
      setTimeout(() => { justResizedRef.current = false; }, 100);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingLeft, resizeLeftDelta, dayWidth, fillDate, baseEmptyDate, assignment.id, onResizeStart]);

  // Clear local deltas once the parent's optimistic update lands, eliminating the visual snap-back
  useEffect(() => { setResizeDelta(0); }, [assignment.projected_empty_date]);
  useEffect(() => { setResizeLeftDelta(0); }, [assignment.fill_date]);

  // Validate dates
  if (!isValid(fillDate)) {
    console.warn('Invalid fill date for assignment:', assignment.id);
    return null;
  }

  // Calculate position and width
  // rawDaysFromStart may be negative when fillDate precedes the grid start
  const rawDaysFromStart = differenceInDays(fillDate, startDate);
  const daysFromStart = Math.max(0, rawDaysFromStart);
  const daysUntilEnd = Math.max(1, differenceInDays(emptyDate, fillDate) + 1);

  // These deltas are 0 when idle, live during drag, and held at final value until the parent
  // optimistic update lands (then cleared by the effects above) — no visual snap-back.
  const effectiveDaysDelta = daysDeltaFromResize;
  const daysDeltaFromLeftResize = Math.trunc(resizeLeftDelta / dayWidth);
  const effectiveLeftDaysDelta = daysDeltaFromLeftResize;

  const left = (daysFromStart + effectiveLeftDaysDelta) * dayWidth;
  // Width shrinks/grows inversely with left movement; also adjustable from right
  const width = Math.max((daysUntilEnd + effectiveDaysDelta - effectiveLeftDaysDelta) * dayWidth, dayWidth);

  // Clamp to visible area. When fillDate precedes startDate the bar is left-clipped:
  // compute the bar's true right edge from rawDaysFromStart so bars starting before
  // the grid don't render wider than their actual date span.
  const visibleLeft = Math.max(0, left);
  const rawLeft = (rawDaysFromStart + effectiveLeftDaysDelta) * dayWidth;
  const barRightPx = rawLeft + width;
  const gridEndPx = (differenceInDays(endDate, startDate) + 1) * dayWidth;
  const visibleWidth = Math.max(0, Math.min(barRightPx, gridEndPx) - visibleLeft);

  // Same-day turnover split: shift left edge right / trim right edge by half a day cell
  const splitLeftOffset = splitRole === 'split-in' ? dayWidth / 2 : 0;
  const splitWidthDelta = splitRole !== null ? -(dayWidth / 2) : 0;
  const adjustedLeft = visibleLeft + splitLeftOffset;
  const adjustedWidth = Math.max(visibleWidth + splitWidthDelta, Math.max(dayWidth / 2, 20));

  const outReasonLabel = getOutReasonLabel(assignment, barLabelMap);

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    left: adjustedLeft,
    width: adjustedWidth,
    opacity: isDragging && !isDragOverlay ? 0.5 : 1,
  };

  // Resolve color: VV code from lot code is most reliable (immune to stale varietal text),
  // then fall back to stored varietal name, then assignment.color override.
  const vvCode = lot?.code && lot.code.length >= 6 ? lot.code.slice(4, 6).toLowerCase() : null;
  const varietyColor = (vvCode && colorMap[vvCode])
    ? colorMap[vvCode]
    : getVarietyColor(colorMap, lot?.varietal);
  const resolvedColor = varietyColor !== VARIETY_DEFAULT_COLOR
    ? varietyColor
    : (assignment.color || VARIETY_DEFAULT_COLOR);

  // Tentative planning lots (not yet received) render with a diagonal stripe overlay
  const isTentative = lot?.is_tentative ?? false;
  const barColorStyle: React.CSSProperties = isMasked
    ? {}
    : isTentative
    ? {
        background: `repeating-linear-gradient(
          -45deg,
          ${resolvedColor} 0px,
          ${resolvedColor} 5px,
          ${darkenHex(resolvedColor, 0.22)} 5px,
          ${darkenHex(resolvedColor, 0.22)} 10px
        )`,
      }
    : { backgroundColor: resolvedColor };
  const textColorClass = 'text-white';
  const textShadow = '0 1px 2px rgba(0,0,0,0.3)';

  const statusClass = STATUS_STYLES[assignment.status] || '';
  const isCompleted = assignment.status === 'completed';
  const canDrag = !isCompleted && !readOnly && !isMasked;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <div
            ref={setNodeRef}
            style={{ ...style, ...barColorStyle, '--bar-left': `${adjustedLeft}px` } as React.CSSProperties}
            className={cn(
              "assignment-bar",
              "absolute top-0.5 bottom-0.5 rounded-sm",
              canDrag ? "cursor-grab active:cursor-grabbing" : isMasked ? "cursor-default" : "cursor-pointer",
              "flex items-center shadow-sm",
              isMasked && "bg-muted-foreground/30 shadow-none",
            textColorClass,
              "hover:shadow-md hover:brightness-110",
              "animate-in fade-in zoom-in-95 duration-300",
              "group",
              statusClass,
              // Disable transitions during resize for instant snapping
              !isResizing && !isResizingLeft && "transition-shadow duration-200 ease-out",
              isSelected && "border-2 border-dashed border-white/70",
              isDragging && "ring-2 ring-primary ring-offset-1 scale-105",
              isDragOverlay && "shadow-lg scale-105",
              (isResizing || isResizingLeft) && "ring-2 ring-primary shadow-lg",
              isConnectionSource && "ring-2 ring-primary ring-offset-1 brightness-110",
              isConnectionTarget && "cursor-crosshair ring-1 ring-primary/50 brightness-110",
              isOtherDragging && "pointer-events-none"
            )}
            onClick={(e) => {
              e.stopPropagation();
              if (justResizedRef.current) return;
              onSelect();
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              if (justResizedRef.current) return;
              onEditRequest(assignment, e.currentTarget.getBoundingClientRect());
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onAssignmentContextMenu) {
                onAssignmentContextMenu(assignment, e.clientX, e.clientY);
              } else if (onMoveToOnDeck) {
                onMoveToOnDeck(assignment);
              }
            }}
            {...(canDrag ? listeners : {})}
            {...(canDrag ? attributes : {})}
          >
            {/* Left-edge resize handle */}
            {!isDragOverlay && !isCompleted && onResizeStart && (
              <div
                className={cn(
                  "w-2 h-full flex items-center justify-center flex-shrink-0",
                  "cursor-ew-resize select-none",
                  "bg-black/15 hover:bg-black/30 rounded-l-sm",
                  "transition-all duration-150",
                  isResizingLeft && "bg-black/40"
                )}
                title="Drag to extend or reduce start date"
                onMouseDown={handleLeftResizeStart}
                onPointerDown={(e) => e.stopPropagation()}
              />
            )}

            {/* Main content - scroll-tracked label + out-reason */}
            <div className="relative flex-1 min-w-0 h-full overflow-hidden">
              {/* Lot code label: stays pinned to the left visible edge of the bar via CSS clamp */}
              {adjustedWidth >= 50 && !isMasked && (
                <span
                  className={cn("absolute top-1/2 -translate-y-1/2 text-xs font-medium whitespace-nowrap pointer-events-none", textColorClass)}
                  style={{
                    left: 'clamp(2px, calc(var(--scroll-left, 0px) - var(--bar-left, 0px) + 2px), calc(100% - 50px))',
                    maxWidth: outReasonLabel && adjustedWidth >= 80 ? 'calc(100% - 52px)' : 'calc(100% - 4px)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textShadow,
                  }}
                >
                  {assignment.display_label ?? lot?.code ?? 'Unknown Lot'}
                  {assignment.fill_volume_l && (
                    <span className="opacity-80 font-normal">
                      {' '}({assignment.fill_volume_l.toLocaleString()} {volumeAbbrev})
                    </span>
                  )}
                </span>
              )}
              {/* Out-reason at right edge */}
              {outReasonLabel && adjustedWidth >= 80 && !isMasked && (
                <span
                  className={cn("absolute right-1 top-1/2 -translate-y-1/2 text-[10px] whitespace-nowrap pointer-events-none font-medium", 'text-white/75')}
                  style={{ textShadow }}
                >
                  {outReasonLabel}
                </span>
              )}
            </div>

            {/* Right side - Resize handle */}
            {!isDragOverlay && !isCompleted && onResize && (
              <div
                className={cn(
                  "w-2 h-full flex items-center justify-center flex-shrink-0",
                  "cursor-ew-resize select-none",
                  "bg-black/15 hover:bg-black/30 rounded-r-sm",
                  "transition-all duration-150",
                  isResizing && "bg-black/40"
                )}
                onMouseDown={handleResizeStart}
                onPointerDown={(e) => e.stopPropagation()}
                title="Drag to extend or reduce duration"
              />
            )}

            {/* Left-edge resize date indicator */}
            {isResizingLeft && (
              <div className="absolute -top-6 left-0 -translate-x-1/2 flex flex-col items-center z-40">
                <div className="px-1.5 py-0.5 text-[10px] bg-primary text-primary-foreground rounded whitespace-nowrap shadow-sm">
                  {format(addDays(fillDate, daysDeltaFromLeftResize), 'MMM d, yyyy')}
                </div>
                <div className="w-px h-2 bg-primary" />
              </div>
            )}

            {/* Right-edge resize date indicator - shown during drag, centered on edge */}
            {isResizing && (
              <div className="absolute -top-6 right-0 translate-x-1/2 flex flex-col items-center z-40">
                <div className="px-1.5 py-0.5 text-[10px] bg-primary text-primary-foreground rounded whitespace-nowrap shadow-sm">
                  {format(addDays(baseEmptyDate, daysDeltaFromResize), 'MMM d, yyyy')}
                </div>
                {/* Connector line to edge */}
                <div className="w-px h-2 bg-primary" />
              </div>
            )}

            {/* Right-edge confirm/cancel buttons - shown after drag ends, centered on edge */}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          {isMasked ? (
            <div className="space-y-1">
              <div className="font-medium">Occupied</div>
              <div className="text-xs">
                {assignment.fill_date} → {assignment.actual_empty_date || assignment.projected_empty_date || 'TBD'}
              </div>
            </div>
          ) : (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">{assignment.display_label ?? lot?.code}</span>
              {isCompleted && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  Completed
                </span>
              )}
              {isTentative && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
                  Planned
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {displayVarietal}
              {lot?.vintage && ` ${lot.vintage}`}
            </div>
            {lot?.client && (
              <div className="text-xs">Client: {lot.client.name}</div>
            )}
            <div className="text-xs">
              {assignment.fill_date} → {assignment.actual_empty_date || assignment.projected_empty_date || 'TBD'}
              {isCompleted && assignment.actual_empty_date && ' (emptied)'}
            </div>
            {assignment.fill_volume_l && (
              <div className="text-xs">Volume: {assignment.fill_volume_l.toLocaleString()} {volumeAbbrev}</div>
            )}
            {showNotes && assignment.notes && (
              <div className="text-xs whitespace-pre-wrap border-t pt-1 mt-1">{assignment.notes}</div>
            )}
          </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Drag overlay version for smooth dragging
// This renders a simplified version without absolute positioning - dnd-kit handles positioning
export function AssignmentBarDragOverlay({
  assignment,
  startDate,
  endDate,
  dayWidth,
  rowHeight,
  colorMap = {},
}: Omit<AssignmentBarProps, 'onClick' | 'isDragOverlay'>) {
  // Calculate width based on duration
  const fillDate = parseISO(assignment.fill_date);
  const emptyDate = assignment.projected_empty_date
    ? parseISO(assignment.projected_empty_date)
    : endDate;

  const daysUntilEnd = Math.max(1, differenceInDays(emptyDate, fillDate) + 1);
  const width = Math.max(daysUntilEnd * dayWidth, 60); // Minimum 60px for readability

  const dragLot = getLotForAssignment(assignment);
  const dragVvCode = dragLot?.code && dragLot.code.length >= 6 ? dragLot.code.slice(4, 6).toLowerCase() : null;
  const dragVarietyColor = (dragVvCode && colorMap[dragVvCode])
    ? colorMap[dragVvCode]
    : getVarietyColor(colorMap, dragLot?.varietal);
  const resolvedColor = dragVarietyColor !== VARIETY_DEFAULT_COLOR
    ? dragVarietyColor
    : (assignment.color || VARIETY_DEFAULT_COLOR);
  const { volumeAbbrev } = useVolumeUnit();

  return (
    <div
      style={{ width, height: rowHeight - 2, backgroundColor: resolvedColor }}
      className={cn(
        "rounded-sm flex items-center shadow-lg text-white",
        "ring-2 ring-primary ring-offset-1",
      )}
    >
      {/* Drag handle visual */}
      <div className="w-2 h-full flex-shrink-0 bg-black/20 rounded-l-sm" />

      {/* Content */}
      <div className="min-w-0 flex-1 overflow-hidden px-1.5">
        <div
          className="text-xs font-medium truncate"
          style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
        >
          {dragLot?.code || 'Unknown Lot'}
          {assignment.fill_volume_l && (
            <span className="opacity-80 font-normal">
              {' '}({assignment.fill_volume_l.toLocaleString()} {volumeAbbrev})
            </span>
          )}
        </div>
      </div>

      {/* Resize handle visual */}
      <div className="w-2 h-full flex-shrink-0 bg-black/20 rounded-r-sm" />
    </div>
  );
}

// Drag overlay for On Deck assignments being dragged onto the timeline
export function OnDeckLotDragOverlay({ assignment, colorMap = {} }: { assignment: VesselAssignmentWithRelations; colorMap?: Record<string, string> }) {
  const lot = getLotForAssignment(assignment);
  const onDeckVvCode = lot?.code && lot.code.length >= 6 ? lot.code.slice(4, 6).toLowerCase() : null;
  const resolvedColor = (onDeckVvCode && colorMap[onDeckVvCode])
    ? colorMap[onDeckVvCode]
    : getVarietyColor(colorMap, lot?.varietal);
  const { volumeAbbrev } = useVolumeUnit();
  const volume = assignment.fill_volume_l ?? lot?.current_volume_l;

  return (
    <div
      style={{ backgroundColor: resolvedColor }}
      className={cn(
        "rounded-md flex items-center gap-2 px-3 py-2 shadow-lg text-white",
        "ring-2 ring-primary ring-offset-1",
        "min-w-[140px]",
      )}
    >
      <Wine className="h-4 w-4 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{lot?.code ?? "Unknown"}</div>
        <div className="text-xs opacity-80 truncate">
          {[lot?.varietal, lot?.vintage].filter(Boolean).join(" ") || "No details"}
        </div>
        {volume && (
          <div className="text-xs opacity-70">
            {Math.round(volume).toLocaleString()} {volumeAbbrev}
          </div>
        )}
      </div>
    </div>
  );
}
